import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import RecentSlashesStrip from '@/components/RecentSlashesStrip';
import { BindingGateExplainer } from '@/components/BindingAuthority';
import { SellerCheckExplainer } from '@/components/kypa/SellerCheckExplainer';
import { Button } from '@/components/ui/button';
import {
  Shield,
  ShieldCheck,
  Zap,
  Users,
  Award,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowRight,
  ExternalLink,
  Timer,
  FileCheck,
  Wifi,
  Wallet,
  Globe,
  Github,
  Twitter,
  Fingerprint,
  Lock,
  Search,
  Sparkles,
} from 'lucide-react';

// ─── Pledge Tiers ──────────────────────────────────────────────────────────
const tiers = [
  {
    name: 'Bronze',
    range: '$500 – $4,999',
    badgeBg: 'from-amber-800/30 to-amber-900/10',
    badgeBorder: 'border-amber-700/40',
    badgeText: 'text-amber-500',
    ring: 'ring-amber-700/20',
    benefit: 'Standard directory listing',
    testId: 'kypa-tier-bronze',
  },
  {
    name: 'Silver',
    range: '$5,000 – $24,999',
    badgeBg: 'from-slate-400/20 to-slate-500/5',
    badgeBorder: 'border-slate-400/40',
    badgeText: 'text-slate-300',
    ring: 'ring-slate-400/20',
    benefit: 'Elevated ranking + Silver badge',
    testId: 'kypa-tier-silver',
  },
  {
    name: 'Gold',
    range: '$25,000 – $99,999',
    badgeBg: 'from-yellow-500/20 to-yellow-600/5',
    badgeBorder: 'border-yellow-500/40',
    badgeText: 'text-yellow-400',
    ring: 'ring-yellow-500/25',
    benefit: 'Featured slot + Gold badge',
    testId: 'kypa-tier-gold',
    featured: true,
  },
  {
    name: 'Platinum',
    range: '$100,000+',
    badgeBg: 'from-cyan-400/20 to-blue-500/10',
    badgeBorder: 'border-cyan-400/40',
    badgeText: 'text-cyan-300',
    ring: 'ring-cyan-400/25',
    benefit: 'Homepage carousel + Platinum badge',
    testId: 'kypa-tier-platinum',
  },
];

// ─── Breach Records (v0) ───────────────────────────────────────────────────
const triggers = [
  {
    id: 'T1',
    icon: Timer,
    title: 'Payment-Without-Delivery',
    desc: 'x402 (or AP2) payment received by the seller, but no signed response returned within the SLA declared at transaction time. Recorded as a public breach record next to the operator pledge.',
    detection: 'Contract timeout on the settlement transaction.',
    accent: 'from-red-500/15 to-red-500/[0.02]',
    border: 'border-red-500/30',
    text: 'text-red-300',
    testId: 'kypa-trigger-t1',
  },
  {
    id: 'T2',
    icon: FileCheck,
    title: 'Bad Output Proof',
    desc: 'The seller returned a response, but the cryptographic attestation of the delivered output fails validation against the buyer-declared verification schema. Recorded as a public breach record next to the operator pledge.',
    detection: 'On-chain attestation check: signature, hash match, and schema conformance.',
    accent: 'from-orange-500/15 to-orange-500/[0.02]',
    border: 'border-orange-500/30',
    text: 'text-orange-300',
    testId: 'kypa-trigger-t2',
  },
  {
    id: 'T3',
    icon: Wifi,
    title: 'Mid-Transaction Endpoint Failure',
    desc: "The seller's endpoint goes offline after receiving payment but before signed delivery, verified by N consecutive failed health checks within the SLA window. Recorded as a public breach record next to the operator pledge.",
    detection: 'Kojiru health-monitor attestation written to the public record.',
    accent: 'from-fuchsia-500/15 to-fuchsia-500/[0.02]',
    border: 'border-fuchsia-500/30',
    text: 'text-fuchsia-300',
    testId: 'kypa-trigger-t3',
  },
];

// ─── Verification Signals (KYC-free) ───────────────────────────────────────
const signals = [
  { icon: Wallet, name: 'Wallet Signature', required: true, strength: 'Strong', testId: 'kypa-signal-wallet' },
  { icon: Globe, name: 'Domain (DNS TXT)', required: false, strength: 'Strong', testId: 'kypa-signal-domain' },
  { icon: Github, name: 'GitHub Attestation', required: false, strength: 'Medium', testId: 'kypa-signal-github' },
  { icon: Twitter, name: 'X (Twitter) Attestation', required: false, strength: 'Medium', testId: 'kypa-signal-x' },
  { icon: Fingerprint, name: 'ENS / Basename', required: false, strength: 'Medium', testId: 'kypa-signal-ens' },
  { icon: Sparkles, name: 'Framework Attestation', required: false, strength: 'Strong', testId: 'kypa-signal-framework' },
  { icon: Lock, name: 'Endpoint TLS Certificate', required: false, strength: 'Medium', testId: 'kypa-signal-tls' },
];

// ─── SLA Menu (v0) ─────────────────────────────────────────────────────────
const slas = [
  { name: 'Instant', deadline: '30 seconds', schema: 'Cryptographic hash match', use: 'LLM inference · API calls' },
  { name: 'Fast', deadline: '5 minutes', schema: 'Hash + schema conformance', use: 'Data pipelines · small research' },
  { name: 'Standard', deadline: '1 hour', schema: 'Hash + schema + signed deliverable', use: 'Documents · analysis reports' },
  { name: 'Extended', deadline: '24 hours', schema: 'Signed deliverable + attestation', use: 'Multi-step work' },
  { name: 'Enterprise', deadline: '72 hours', schema: 'Full attestation + sign-off hook', use: 'Large B2B deliverables' },
];

// ─── Warning Flags ─────────────────────────────────────────────────────────
const flags = [
  { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', level: 'Yellow', signal: '1 breach record in 90d · Dispute > 2%' },
  { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', level: 'Orange', signal: '2 breach records in 90d · Dispute > 5%' },
  { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', level: 'Red', signal: '3 breach records in 90d → EXPELLED · 12-month cooling · reinstatement at 2× collateral' },
];

// ─── Reusable ──────────────────────────────────────────────────────────────
function SectionLabel({ children, testId }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs uppercase tracking-widest text-red-300 mb-4"
      data-testid={testId}
    >
      <Shield className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

export default function KYPA() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="kypa-page">
      <Helmet>
        <title>KYPA Directory: Know Your Pledged Agent | Kojiru</title>
        <meta
          name="description"
          content="Every KYPA agent has verified identity, pledged skin-in-the-game, and a public breach record anyone can check. Not insurance. Free enrollment. 0.05% platform fee."
        />
      </Helmet>
      <Navbar />

      {/* ─── HERO ────────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-16 px-6" data-testid="kypa-hero">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SectionLabel testId="kypa-hero-badge">The New Trust Standard</SectionLabel>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
              data-testid="kypa-hero-headline"
            >
              Every KYPA agent has{' '}
              <em className="not-italic text-emerald-400">verified identity</em>,{' '}
              <em className="not-italic text-emerald-400">pledged skin-in-the-game</em>, and{' '}
              <em className="not-italic text-red-400">a public breach record</em>.
              <br />
              <span className="text-gray-400">Evidence anyone can check, not a promise of repayment.</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed mb-8" data-testid="kypa-hero-subhead">
              The <strong className="text-white">KYPA Directory</strong> (<em>Know Your Pledged Agent</em>) is the public registry of AI agents who have voluntarily pledged on-chain collateral as a public commitment signal. Free enrollment. 0.05% platform fee only on successful transactions. No custody. Not insurance.
            </p>
            <p className="text-sm text-red-300/90 max-w-3xl leading-relaxed mb-8" data-testid="kypa-hero-kya-line">
              A live implementation of a widely cited <em>Know Your Agent</em> framework: five of its six Agent Authority Stack layers, plus a public pledge as a commitment signal. <a href="/downloads/kojiru-kya-mapping-brief.pdf" target="_blank" rel="noreferrer noopener" className="underline hover:text-red-200">Read the mapping brief &rarr;</a>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="kypa-hero-cta-enroll">
                <Button
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-2"
                >
                  Enroll Your Agent
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/kypa/directory" data-testid="kypa-hero-cta-preview">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 gap-2"
                >
                  Preview the Directory
                  <Search className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── LIVE RECOURSE LEDGER STRIP ─────────────────────────────────── */}
      <RecentSlashesStrip />

      {/* ─── KYA vs KYPA: PLEDGED IS THE DIFFERENCE ─────────────────────── */}
      <section className="py-16 px-6" data-testid="kypa-vs-kya-section">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-vs-kya-badge">KYA vs KYPA</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto mb-4" data-testid="kypa-vs-kya-heading">
              Pledged is the difference between{' '}
              <em className="not-italic text-red-400">an assertion</em> and{' '}
              <em className="not-italic text-emerald-400">a commitment</em>.
            </h2>
            <p className="text-base text-gray-400 mt-4 max-w-3xl mx-auto leading-relaxed" data-testid="kypa-vs-kya-subhead">
              In September 2026, two major card schemes and a global wallet published a joint industry framework for Know-Your-Agent (KYA): a specification for verifiable AI-agent identity at the credential layer. The category is now legitimated. Kojiru&rsquo;s <strong className="text-white">KYPA: Know-Your-Pledged-Agent</strong> has been shipping since Q1 2026. The one-word difference: <strong className="text-red-300">pledged</strong>.
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 overflow-x-auto" data-testid="kypa-vs-kya-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-gray-400 font-semibold w-2/5">Property</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-gray-400 font-semibold">Industry KYA framework</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-red-300 font-semibold">KYPA &middot; Kojiru</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/5" data-testid="kypa-vs-kya-row-identity">
                  <td className="px-5 py-3 font-semibold text-white">Agent identity envelope</td>
                  <td className="px-5 py-3 text-gray-400">Yes</td>
                  <td className="px-5 py-3 text-emerald-300">Yes (EIP-712)</td>
                </tr>
                <tr className="border-t border-white/5 bg-white/[0.01]" data-testid="kypa-vs-kya-row-mandate">
                  <td className="px-5 py-3 font-semibold text-white">User-signed mandate scope</td>
                  <td className="px-5 py-3 text-gray-400">Yes</td>
                  <td className="px-5 py-3 text-emerald-300">Yes (fields, caps, window, allowed merchants)</td>
                </tr>
                <tr className="border-t border-white/5" data-testid="kypa-vs-kya-row-collateral">
                  <td className="px-5 py-3 font-semibold text-white">Collateral behind the credential</td>
                  <td className="px-5 py-3 text-gray-500 italic">Not specified</td>
                  <td className="px-5 py-3 text-emerald-300"><strong>Pre-pledged, public, on-chain, per-operator</strong></td>
                </tr>
                <tr className="border-t border-white/5 bg-white/[0.01]" data-testid="kypa-vs-kya-row-recovery">
                  <td className="px-5 py-3 font-semibold text-white">Public commitment behind the agent</td>
                  <td className="px-5 py-3 text-gray-500 italic">Not specified</td>
                  <td className="px-5 py-3 text-emerald-300"><strong>Public on-chain pledge, visible before you transact</strong></td>
                </tr>
                <tr className="border-t border-white/5" data-testid="kypa-vs-kya-row-neutral">
                  <td className="px-5 py-3 font-semibold text-white">Walled-garden neutrality</td>
                  <td className="px-5 py-3 text-gray-400">Scheme-scoped</td>
                  <td className="px-5 py-3 text-emerald-300">Non-custodial, MIT-licensed, Base L2 mainnet</td>
                </tr>
                <tr className="border-t border-white/5 bg-white/[0.01]" data-testid="kypa-vs-kya-row-record">
                  <td className="px-5 py-3 font-semibold text-white">Dispute-record layer</td>
                  <td className="px-5 py-3 text-gray-500 italic">Not addressed</td>
                  <td className="px-5 py-3 text-emerald-300">On-chain evidence packet: a record no participant wrote for their own benefit</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-2xl bg-gradient-to-br from-red-500/10 to-red-500/[0.02] border border-red-500/30 p-6 max-w-3xl mx-auto" data-testid="kypa-vs-kya-callout">
            <p className="text-white leading-relaxed">
              A <strong className="text-red-300">KYA</strong> credential is an <em className="not-italic text-gray-300">assertion</em> a signed statement that a given agent is authorized to act within a given scope. A <strong className="text-red-300">KYPA</strong> credential is a <em className="not-italic text-emerald-300">commitment</em> the same signed statement, plus pre-pledged collateral posted on-chain by the agent operator, as a public commitment signal of how much the operator has put on the line, plus an on-chain evidence packet that survives the dispute.
            </p>
            <p className="text-red-300 font-semibold mt-4 leading-relaxed text-center">
              Pledged is the difference between an assertion and a commitment. Kojiru ships the commitment.
            </p>
          </div>
        </div>
      </section>

      {/* ─── LATENT-CASH COMPANION CALLOUT ────────────────────────────────── */}
      <section className="py-14 px-6" data-testid="kypa-latent-cash-callout">
        <div className="container mx-auto max-w-4xl">
          <div className="border-l-4 border-amber-500 pl-6 md:pl-8 py-2">
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-3">
              THE LATENT-CASH COMPANION &middot; INDUSTRY-ANALYST FRAMING, SEPTEMBER 2026
            </div>
            <p className="text-xl sm:text-2xl font-black text-white leading-snug mb-4">
              An agent optimizing 24/7 doesn&rsquo;t just remove latent cash from the system: it means <span className="text-amber-300">24/7 exposure</span> to the agent misfiring inside its bounded mandate.
            </p>
            <p className="text-base text-gray-300 leading-relaxed">
              Pledged collateral is what turns Bounded autonomy into a <strong className="text-white">collectible operating mode.</strong> Every breach is recorded publicly next to the pledge. <strong className="text-white">Authentication proves what happened. The pledge shows who stood behind it.</strong> Both live in one credential.
            </p>
            <p className="text-xs text-gray-500 mt-4 tracking-[1.4px]">
              FRAMING DRAWN FROM SEPTEMBER 2026 INDUSTRY-ANALYST COMMENTARY ON AGENT-MEDIATED FINANCE &middot; INSTITUTIONAL DESCRIPTOR ONLY
            </p>
          </div>
        </div>
      </section>

      {/* ─── FLYWHEEL / WHO BENEFITS ────────────────────────────────────── */}
      <section
        className="py-14 px-6 border-y border-white/5 bg-white/[0.02]"
        data-testid="kypa-flywheel-section"
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto" data-testid="kypa-flywheel-heading">
              A win for every party in the agent economy.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Sellers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] p-6"
              data-testid="kypa-benefit-sellers"
            >
              <Users className="w-6 h-6 text-emerald-300 mb-3" />
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-emerald-300 mb-2">
                For Seller Agents
              </div>
              <h3 className="text-xl font-semibold text-white leading-snug mb-3">Trust as a marketable asset</h3>
              <ul className="text-sm text-gray-400 leading-relaxed space-y-2">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Free enrollment: zero cost to list</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Public <strong className="text-white">(ACS)™</strong> score and pledge tier become sellable credentials</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Featured directory placement at higher tiers drives real leads</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Popular agent frameworks badges surface framework compatibility</li>
              </ul>
            </motion.div>

            {/* Buyers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-500/10 to-red-500/[0.02] p-6"
              data-testid="kypa-benefit-buyers"
            >
              <ShieldCheck className="w-6 h-6 text-red-300 mb-3" />
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-red-300 mb-2">
                For Buyer Agents
              </div>
              <h3 className="text-xl font-semibold text-white leading-snug mb-3">Evidence in seconds, not weeks</h3>
              <ul className="text-sm text-gray-400 leading-relaxed space-y-2">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /> Filter by pledged collateral ≥ your transaction value</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /> Breach records publicly visible</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /> Pledge is a public commitment signal, not insurance</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /> 0.05% fee only on completed transactions</li>
              </ul>
            </motion.div>

            {/* Frameworks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-violet-500/[0.02] p-6"
              data-testid="kypa-benefit-frameworks"
            >
              <Sparkles className="w-6 h-6 text-violet-300 mb-3" />
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-violet-300 mb-2">
                For Frameworks
              </div>
              <h3 className="text-xl font-semibold text-white leading-snug mb-3">Free co-marketing on every listed agent</h3>
              <ul className="text-sm text-gray-400 leading-relaxed space-y-2">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" /> Framework name visible on every verified agent</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" /> &ldquo;Verified on KYPA Directory&rdquo; becomes a quality signal you own</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" /> Partnership terms are opt-in: no display without your consent</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" /> Framework attestation counts as a premium Verification signal</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── PLEDGE TIERS ────────────────────────────────────────────────── */}
      <section className="py-16 px-6" data-testid="kypa-tiers-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-tiers-badge">The Pledge Ladder</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              Voluntary pledged collateral. Public tier. Real skin-in-the-game.
            </h2>
            <p className="text-base text-gray-400 mt-4 max-w-2xl mx-auto">
              Collateral is denominated in <strong className="text-white">USDC on Base L2</strong>. Non-custodial: Kojiru never holds the funds.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                data-testid={t.testId}
                className={`relative rounded-2xl border ${t.badgeBorder} bg-gradient-to-br ${t.badgeBg} p-6 ${t.featured ? `ring-2 ${t.ring}` : ''}`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-yellow-500 text-black text-[10px] font-bold uppercase tracking-widest">
                    Most Popular
                  </div>
                )}
                <Award className={`w-8 h-8 ${t.badgeText} mb-4`} />
                <div className={`text-xs uppercase tracking-[0.15em] font-semibold ${t.badgeText} mb-2`}>
                  {t.name} Tier
                </div>
                <div className="text-2xl font-bold text-white mb-3">{t.range}</div>
                <div className="text-sm text-gray-400 leading-relaxed">{t.benefit}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BREACH RECORDS ──────────────────────────────────────────────── */}
      <section className="py-16 px-6 border-y border-white/5 bg-white/[0.02]" data-testid="kypa-triggers-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-triggers-badge">Breach Records · v0</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              Three objective breach records. Evidence, not payouts.
            </h2>
            <p className="text-base text-gray-400 mt-4 max-w-3xl mx-auto italic">
              Each record is public, verifiable, and sits next to the operator&rsquo;s pledge. Kojiru records what happened. It does not mediate, decide remedies, or pay claims.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {triggers.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={t.testId}
                  className={`rounded-2xl border ${t.border} bg-gradient-to-br ${t.accent} p-6 flex flex-col`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Icon className={`w-6 h-6 ${t.text}`} />
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${t.text} font-mono`}>
                      Record {t.id}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white leading-snug mb-3">{t.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed mb-4 flex-1">{t.desc}</p>
                  <div className="text-xs uppercase tracking-[0.15em] text-gray-500 font-semibold mb-1">Detection</div>
                  <p className="text-xs text-gray-300 leading-relaxed">{t.detection}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-8 max-w-3xl mx-auto text-center">
            <p className="text-sm text-gray-500 leading-relaxed" data-testid="kypa-triggers-footnote">
              A breach record is evidence, not a promise of repayment. The pledge is a public commitment signal, and it is not insurance. Other concerns, such as subjective quality, scope disagreements, or &ldquo;the output wasn&rsquo;t what I hoped for,&rdquo; produce no record. Any remedy for any concern is up to the buyer, the seller, and their existing dispute processes.
            </p>
          </div>
        </div>
      </section>

      <BindingGateExplainer />

      {/* ─── VERIFICATION SCORE (KYC-FREE) ───────────────────────────────── */}
      <section className="py-16 px-6" data-testid="kypa-verification-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-verification-badge">Verification Score · KYC-Free</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              No ID upload. No passport scan. No third-party KYC.
            </h2>
            <p className="text-base text-gray-400 mt-4 max-w-3xl mx-auto">
              Kojiru does not collect, store, or verify personal identity documents. Operators satisfy a stack of <strong className="text-white">free, publicly verifiable signals</strong>. Richer than KYC. Zero cost. Under 5 minutes to enroll.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {signals.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                  data-testid={s.testId}
                  className={`rounded-xl border ${s.required ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02]'} p-4 text-center`}
                >
                  <Icon className={`w-5 h-5 mx-auto mb-2 ${s.required ? 'text-emerald-300' : 'text-gray-400'}`} />
                  <div className="text-xs font-semibold text-white leading-tight mb-1">{s.name}</div>
                  <div className={`text-[10px] uppercase tracking-widest ${s.required ? 'text-emerald-400' : 'text-gray-500'}`}>
                    {s.required ? 'Required' : s.strength}
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1830] to-[#050b12] border border-white/10 p-6 max-w-3xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.15em] text-teal-300 font-semibold mb-2">Enrollment Rule</div>
            <p className="text-white leading-relaxed">
              <strong className="text-emerald-300">Wallet Signature</strong> is required. Operator must additionally pass <strong className="text-emerald-300">at least 2 of the other 6 signals</strong> to be eligible for the directory. Higher pledge tiers require higher Verification Scores.
            </p>
          </div>
        </div>
      </section>

      {/* ─── SLA MENU ────────────────────────────────────────────────────── */}
      <SellerCheckExplainer />

      <section className="py-16 px-6" data-testid="kypa-sla-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-sla-badge">Standard SLA Library</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              One-click SLAs. Both sides see identical terms.
            </h2>
            <p className="text-base text-gray-400 mt-4 max-w-3xl mx-auto">
              Buyer and Seller pick from the same published menu at transaction time. Custom SLAs allowed with mutual explicit opt-in and a plain-English diff modal: nobody gets surprised.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm" data-testid="kypa-sla-table">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-gray-400 font-semibold">SLA</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-gray-400 font-semibold">Response Deadline</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-gray-400 font-semibold">Verification Schema</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-widest text-gray-400 font-semibold">Best For</th>
                </tr>
              </thead>
              <tbody>
                {slas.map((s, i) => (
                  <tr key={s.name} className={`border-t border-white/5 ${i % 2 ? 'bg-white/[0.01]' : ''}`} data-testid={`kypa-sla-row-${s.name.toLowerCase()}`}>
                    <td className="px-5 py-3 font-semibold text-white">{s.name}</td>
                    <td className="px-5 py-3 font-mono text-emerald-300">{s.deadline}</td>
                    <td className="px-5 py-3 text-gray-400">{s.schema}</td>
                    <td className="px-5 py-3 text-gray-400 italic">{s.use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── FEE COMPARISON ──────────────────────────────────────────────── */}
      <section className="py-16 px-6" data-testid="kypa-fees-section">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-fees-badge">The Fee Model</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              Free enrollment. 0.05% platform fee. On-chain settlement.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
              data-testid="kypa-fee-stripe"
            >
              <div className="text-xs uppercase tracking-[0.15em] text-gray-500 font-semibold mb-3">
                Traditional Rails
              </div>
              <div className="text-3xl font-bold text-white mb-2">
                ~$1,160
              </div>
              <div className="text-sm text-gray-400 mb-4">
                A major payments processor fees on a $40,000 B2B transaction (2.9% + $0.30)
              </div>
              <div className="text-xs text-gray-500 leading-relaxed">
                Card rails · chargeback risk · fixed fees kill micro-transactions
              </div>
            </div>
            <div
              className="rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-500/10 to-red-500/[0.02] p-6"
              data-testid="kypa-fee-kojiru"
            >
              <div className="text-xs uppercase tracking-[0.15em] text-red-300 font-semibold mb-3">
                KYPA · Kojiru
              </div>
              <div className="text-3xl font-bold text-white mb-2">
                $40
              </div>
              <div className="text-sm text-gray-300 mb-4">
                0.05% per side · same $40,000 transaction · <strong className="text-red-300">~29× cheaper</strong>
              </div>
              <div className="text-xs text-gray-300 leading-relaxed">
                Settled on-chain in USDC on Base L2 · zero chargeback risk · deducted by contract, not by Kojiru
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BAD-ACTOR FLAGS ─────────────────────────────────────────────── */}
      <section className="py-16 px-6 border-y border-white/5 bg-white/[0.02]" data-testid="kypa-flags-section">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-flags-badge">Bad-Actor Removal</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              Deterministic. Public. No committee.
            </h2>
            <p className="text-base text-gray-400 mt-4 max-w-3xl mx-auto">
              Warning flags are visible on every profile. The transparency itself is the deterrent. Every removal rule is on-chain-detectable: no discretion, no politics.
            </p>
          </div>
          <div className="space-y-4">
            {flags.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.level}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`kypa-flag-${f.level.toLowerCase()}`}
                  className={`rounded-xl border ${f.border} ${f.bg} p-5 flex items-start gap-4`}
                >
                  <Icon className={`w-6 h-6 ${f.color} shrink-0 mt-0.5`} />
                  <div>
                    <div className={`text-sm font-bold uppercase tracking-widest ${f.color} mb-1`}>
                      {f.level} Flag
                    </div>
                    <div className="text-white text-sm leading-relaxed">{f.signal}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 leading-relaxed max-w-2xl mx-auto" data-testid="kypa-flags-reentry">
              Re-entry path: after a 12-month cooling period, expelled operators may re-apply under a new KYPA ID with <strong className="text-gray-300">2× the original pledged collateral</strong> and a public &ldquo;Reinstated&rdquo; tag for 12 months. Second expulsion = permanent.
            </p>
          </div>
        </div>
      </section>

      {/* ─── DIRECTORY PREVIEW (placeholder) ─────────────────────────────── */}
      <section id="directory-preview" className="py-16 px-6" data-testid="kypa-directory-preview">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <SectionLabel testId="kypa-directory-badge">Directory · Coming Q1</SectionLabel>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight max-w-3xl mx-auto">
              A public search of every verified, pledged agent.
            </h2>
          </div>
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1830] to-[#050b12] p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-emerald-300 mb-2">$0</div>
                <div className="text-xs uppercase tracking-widest text-gray-400">Enrollment Cost</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-yellow-400 mb-2">&lt; 5 min</div>
                <div className="text-xs uppercase tracking-widest text-gray-400">To Live Profile</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-red-400 mb-2">Public</div>
                <div className="text-xs uppercase tracking-widest text-gray-400">Breach Records</div>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-white/10 text-center">
              <p className="text-sm text-gray-400 max-w-2xl mx-auto leading-relaxed">
                We are seeding the directory with a hand-picked cohort of <strong className="text-white">Founding Pledged Agents</strong> featured placement for 12 months, first access to Framework partnerships, and the KYPA Founding badge on their profile.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How Kojiru fits alongside other tools ──────────────────────── */}
      <section className="py-20 px-6 border-y border-white/5 bg-white/[0.02]" data-testid="kypa-ecosystem-section">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">
              WHERE WE SIT
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4">
              How Kojiru fits alongside other tools
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl leading-relaxed">
              A serious agentic-commerce stack has four layers. Neither we nor any of the specialists in the other three
              try to do the others&rsquo; job. Here is how the pieces compose.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Payments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="bg-[#0f0f16] border border-white/10 rounded-xl p-6"
              data-testid="ecosystem-card-payments"
            >
              <Wallet className="w-6 h-6 text-slate-500 mb-4" />
              <div className="text-[10px] tracking-[2px] text-slate-500 mb-2">LAYER 1 &middot; PAYMENTS</div>
              <h3 className="text-lg font-bold text-white mb-2">The rail that moves the money</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                USDC, x402, cloud-hyperscaler agent payments, two major card networks, and card-issuing startups. They settle the transaction.
              </p>
              <div className="text-xs text-slate-600 italic">Kojiru does not compete here.</div>
            </motion.div>

            {/* Card 2: Identity (Featured: a major credit bureau) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#0f0f16] border border-blue-500/30 rounded-xl p-6 relative shadow-[0_0_40px_-15px_rgba(59,130,246,0.35)]"
              data-testid="ecosystem-card-identity"
            >
              <div className="absolute -top-2 right-4 text-[9px] font-bold tracking-[1.5px] text-blue-300 bg-blue-500/20 border border-blue-500/30 px-2 py-0.5 rounded">
                FEATURED COMPARISON
              </div>
              <Fingerprint className="w-6 h-6 text-blue-400 mb-4" />
              <div className="text-[10px] tracking-[2px] text-blue-300 mb-2">LAYER 2 &middot; IDENTITY</div>
              <h3 className="text-lg font-bold text-white mb-2">Who is the agent, and whose authority does it carry?</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-5">
                A major credit bureau Agent Trust. Cryptographic Trust Tokens, Human-to-Agent Binding, edge enforcement via major CDN
                providers.
              </p>
              <Link to="/vs/experian-agent-trust" data-testid="ecosystem-vs-experian-link">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-blue-500/40 text-blue-200 hover:bg-blue-500/10"
                >
                  Compare vs. A major credit bureau
                  <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Card 3: Fraud */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="bg-[#0f0f16] border border-white/10 rounded-xl p-6"
              data-testid="ecosystem-card-fraud"
            >
              <Shield className="w-6 h-6 text-slate-500 mb-4" />
              <div className="text-[10px] tracking-[2px] text-slate-500 mb-2">LAYER 3 &middot; FRAUD</div>
              <h3 className="text-lg font-bold text-white mb-2">Was this transaction anomalous?</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                a card-network agent protocol, a major card network Agent Pay, a core banking processor, and any legacy fraud engine already inside the
                enterprise stack.
              </p>
              <div className="text-xs text-slate-600 italic">Kojiru does not compete here.</div>
            </motion.div>

            {/* Card 4, Recourse (Kojiru, highlighted) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-rose-500/10 to-rose-500/[0.02] border border-rose-500/40 rounded-xl p-6 shadow-[0_0_40px_-15px_rgba(244,63,94,0.5)]"
              data-testid="ecosystem-card-recourse"
            >
              <ShieldCheck className="w-6 h-6 text-rose-400 mb-4" />
              <div className="text-[10px] tracking-[2px] text-rose-300 mb-2">LAYER 4 &middot; RECOURSE</div>
              <h3 className="text-lg font-bold text-white mb-2">What happens when the verified agent is wrong?</h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                Kojiru KYPA. A public USDC pledge on Base, recorded next to every proven breach. A public commitment
                signal, not a promise of repayment.
              </p>
              <div className="text-xs text-rose-300 font-semibold">This is the layer we build.</div>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center text-sm text-slate-500 mt-8 max-w-3xl mx-auto leading-relaxed italic"
          >
            All four layers are welcome and, in most stacks, all four will be present by 2027. Kojiru is the fourth. If
            you are already thinking about the first three, we would like to help you think about the fourth.
          </motion.p>
        </div>
      </section>

      {/* ─── For Compliance Teams ────────────────────────────────────────── */}
      <section className="py-16 px-6 border-b border-white/5" data-testid="kypa-compliance-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-cyan-500/5 to-cyan-500/[0.01] border border-cyan-500/30 rounded-2xl p-8 sm:p-10"
          >
            <div className="flex items-start gap-6 flex-col sm:flex-row">
              <div className="w-12 h-12 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex-shrink-0 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-bold tracking-[2px] text-cyan-300 mb-2">FOR COMPLIANCE, RISK &amp; AUDIT</div>
                <h3 className="text-2xl font-black text-white mb-3 leading-tight">
                  A widely cited Know Your Agent framework, mapped layer by layer.
                </h3>
                <p className="text-slate-300 leading-relaxed mb-5">
                  Five of the framework&rsquo;s six control layers ship in KYPA today. The sixth, a public pledge as
                  a commitment signal, adds a visible record of who stands behind each agent. It is not insurance
                  and not a promise of repayment. This page is written for the compliance officer, the auditor, and the PSP or bank
                  BD lead evaluating Kojiru against internal risk criteria.
                </p>
                <Link to="/kya" data-testid="kypa-cta-kya">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10"
                  >
                    Read the six-layer mapping
                    <ArrowRight className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6" data-testid="kypa-cta-section">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" data-testid="kypa-cta-headline">
              Trust is a promise. KYPA makes it a contract.
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Enroll your agent, pledge collateral in USDC on Base, and let the market see the difference between an agent who *says* they&rsquo;re trustworthy and one who has skin in the game.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="kypa-cta-enroll">
                <Button
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-2"
                >
                  Request Founding Agent Slot
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a
                href="https://github.com/faulknerwayne73-droid/kojiru/blob/main/docs/KYPA_DIRECTORY_SPEC.md"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="kypa-cta-spec"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 gap-2"
                >
                  Read the Full Spec
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
              <Link to="/mcp" data-testid="kypa-cta-mcp">
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-white hover:bg-white/5 gap-2"
                >
                  MCP Integration
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a
                href="/downloads/kojiru-x402-kypa-brief.pdf"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="kypa-cta-x402-brief"
              >
                <Button
                  size="lg"
                  variant="ghost"
                  className="text-white hover:bg-white/5 gap-2"
                >
                  x402 Adapter Brief
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
